import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, Crown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('No pudimos verificar el pago. Contacta soporte.');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/status/${sid}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('¡Bienvenido a Premium!');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts >= maxAttempts - 1) {
        setStatus('error');
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div className="animate-fade-in-up">
            <Loader2 className="w-16 h-16 text-amber-500 mx-auto mb-6 animate-spin" />
            <h1 className="font-['Playfair_Display'] text-2xl font-bold text-slate-900 mb-2">
              Verificando pago...
            </h1>
            <p className="text-slate-600">
              Por favor espera mientras confirmamos tu suscripción
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full mb-4">
              <Crown className="w-4 h-4" />
              <span className="text-sm font-medium">Premium</span>
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-slate-900 mb-2">
              ¡Bienvenido a Premium!
            </h1>
            <p className="text-slate-600 mb-8">
              Tu suscripción ha sido activada. Ya tienes acceso a todas las funcionalidades premium.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6"
                data-testid="go-dashboard-btn"
              >
                Ir al Dashboard
              </Button>
              <Button
                onClick={() => navigate('/speaking')}
                variant="outline"
                className="w-full rounded-full py-6"
                data-testid="try-speaking-btn"
              >
                Probar Speaking con Voz
              </Button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">😕</span>
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl font-bold text-slate-900 mb-2">
              Algo salió mal
            </h1>
            <p className="text-slate-600 mb-8">
              No pudimos verificar tu pago. Si crees que es un error, contacta nuestro soporte.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/subscription')}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6"
              >
                Intentar de nuevo
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full rounded-full py-6"
              >
                Volver al Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
